import { Badge, Card } from "components/ui";
import { SessionContext } from "contexts/SessionContext";
import React, { useContext } from "react";
import { Trans } from "react-i18next";
import { HiOutlineSupport } from "react-icons/hi";
import { HiOutlineBriefcase, HiOutlineCpuChip, HiOutlineTag, HiOutlineUser } from "react-icons/hi2";
import { MdOutlineVideoCameraBack, MdOutlineVideoCameraFront } from "react-icons/md";
import { RiImageAiLine, RiVideoOnAiLine } from "react-icons/ri";
import { TbApi, TbCalendarBolt, TbCode, TbDatabase, TbHeartStar, TbPhotoAi, TbTagStarred, TbUserCheck } from "react-icons/tb";
import { getGB } from "utils/bytesUtils";
import { formatNumber } from "utils/formatNumber";
import { AI_STUDIO_PLANS } from "views/subscription";
import { socialNetworks } from "views/calendar/SocialNetworkSidePanel";
import { SocialIcon } from "react-social-icons";

const ProductPlan = ({index = 0, item, selectedPlan, billingFrequency, setSelectedPlan}) => {

    const { user } = useContext(SessionContext);

    const discountText = process.env.REACT_APP_DEFAULT_DISCOUNT;
    const discountFactor = parseFloat(discountText) ? 1 - parseFloat(discountText) / 100 : 1;
    const isAIStudioPlan = AI_STUDIO_PLANS.includes(item?.name);

    const handleOnSelect = () => {
        setSelectedPlan(item.id);
    }

    return (
        <Card 
            onClick={handleOnSelect} 
            bodyClass={`flex flex-col lg:flex-row items-center gap-4 !px-4 lg:!px-7 !py-4 relative`} 
            className={`
                !my-4 lg:!my-0 shrink-0 rounded-3xl overflow-hidden !px-0 hover:bg-tertiary-50 dark:hover:bg-gray-700 box-border cursor-pointer transition-all !border-0 
                ${selectedPlan == item.id 
                    ? `group is-active bg-gradient-primary dark:!opacity-100 shadow-xl shadow-primary-600/50 dark:shadow-primary-600/10` 
                    : 'bg-secondary-500'
                }
            `}
        >
            <div className='w-[110px] text-left'>
                <img 
                    src={`/img/others/plan-${item?.name}.png`} 
                    className="w-full h-24 my-[0.75px] object-contain z-10 relative drop-shadow-lg" 
                    alt={item?.label}
                />
            </div>
            <div className='relative z-10 w-[140px]'>
                {user?.subscription && user?.subscription?.items?.filter(e => e.product.id == item.id)?.length > 0 && <span className='bg-gradient-primary text-secondary-500 group[.is-active]:bg-secondary-500 group[.is-active]:text-tertiary-900 font-bold px-3 py-1 rounded-xl w-fit !text-xs inline-block !mb-2'>Actual Plan</span>}
                <h3 className='mega-title !text-2xl text-primary-600 text-center lg:text-left !font-bold group-[.is-active]:!text-gray-50'>{item?.label}</h3>
                <p className='group-[.is-active]:text-tertiary-100 text-center lg:text-left text-tertiary-500 dark:text-gray-300 text-xs'>{item?.description}</p>
            </div>
            <div className='flex flex-col gap-2.5 px-6 mt-2 md:mt-0 text-xs'>
                {isAIStudioPlan ? (
                    <>
                        {/* AI Media Credits - EMPHASIZED */}
                        <div className="flex flex-col items-center justify-center">
                            <div className='flex items-center gap-3 py-2 px-3 bg-primary-600/10 group-[.is-active]:bg-white/10 rounded-xl'>
                                <TbPhotoAi className='text-primary-600 group-[.is-active]:!text-primary-400 text-3xl'/> 
                                <div className='flex flex-col'>
                                    <span className='group-[.is-active]:!text-tertiary-50 text-primary-700 dark:text-primary-400 font-bold text-lg leading-tight'>{formatNumber(item?.images_amount)}</span>
                                    <span className='group-[.is-active]:!text-tertiary-200 text-tertiary-600 dark:text-gray-400 text-xs'><Trans i18nKey={`subscription.aiImages`}>AI Media Credits</Trans></span>
                                </div>
                            </div>
                        </div>
                        {(!!item?.white_label_enabled || !!item?.api_keys_enabled) && (
                            <div className="flex items-center gap-2 opacity-75">
                                {item?.white_label_enabled && (
                                    <div className='flex items-center gap-2'>
                                        <TbTagStarred className='text-primary-600 group-[.is-active]:!text-primary-500 text-lg'/>
                                        <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b><Trans i18nKey={`subscription.whiteLabel`}>White Label</Trans></b></span>
                                    </div>
                                )}
                                {!!item?.white_label_enabled && !!item?.api_keys_enabled && <span className="group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300">+</span>}
                                {item?.api_keys_enabled && (
                                    <div className='flex items-center gap-2'>
                                        <TbCode className='text-primary-600 group-[.is-active]:!text-primary-500 text-lg'/>
                                        <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b><Trans i18nKey={`subscription.api`}>API Access</Trans></b></span>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Default layout for Social Plans */}
                        <div className='flex items-center gap-2'>
                            <HiOutlineUser className='text-primary-600 group-[.is-active]:!text-primary-500 text-lg'/> 
                            <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b>{item?.users_amount}</b> <Trans i18nKey={`subscription.productUser`}>User</Trans>{item?.users_amount > 1 && 's'}</span>
                        </div>
                        <div className='flex items-center gap-2'>
                            <HiOutlineBriefcase className='text-primary-600 group-[.is-active]:!text-primary-500 text-xl'/> 
                            <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b>{item?.companies_amount}</b> {item?.users_amount > 1 ? <Trans i18nKey={`subscription.companies`}>Companies</Trans> : <Trans i18nKey={`subscription.company`}>Company</Trans>}</span>
                        </div>
                        <div className='flex items-center gap-2'>
                            <TbPhotoAi className='text-primary-600 group-[.is-active]:!text-primary-500 text-xl'/> 
                            <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b>{formatNumber(item?.images_amount)}</b> <Trans i18nKey={`subscription.aiImages`}>AI Media Credits</Trans></span>
                        </div>
                        <div className='flex items-center gap-2'>
                            <HiOutlineCpuChip className='text-primary-600 group-[.is-active]:!text-primary-500 text-xl'/> 
                            <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b>{formatNumber(item?.words_amount)}</b> <Trans i18nKey={`subscription.aiGeneratedWords`}>AI Text Credits</Trans></span>
                        </div>
                        {(!!item?.white_label_enabled || !!item?.api_keys_enabled) && (
                            <div className="flex items-center gap-2">
                                {item?.white_label_enabled && (
                                    <div className='flex items-center gap-2'>
                                        <TbTagStarred className='text-primary-600 group-[.is-active]:!text-primary-500 text-xl'/>
                                        <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b><Trans i18nKey={`subscription.whiteLabel`}>White Label</Trans></b></span>
                                    </div>
                                )}
                                {!!item?.white_label_enabled && !!item?.api_keys_enabled && <span className="group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300">+</span>}
                                {item?.api_keys_enabled && (
                                    <div className='flex items-center gap-2'>
                                        <TbCode className='text-primary-600 group-[.is-active]:!text-primary-500 text-xl'/>
                                        <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><b><Trans i18nKey={`subscription.api`}>API Access</Trans></b></span>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
                {/* <div className='flex items-center gap-2'>
                    <TbDatabase className='text-primary-600 text-xl'/> 
                    <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'>{formatNumber(getGB(item?.bytes_amount))} GB <Trans i18nKey={`subscription.storage`}>Storage</Trans></span>
                </div> */}
                {/* <div className='flex items-center gap-2'>
                    <HiOutlineAtSymbol className='text-primary-600 text-xl'/> 
                    <span className='group-[.is-active]:!text-tertiary-50 flex items-center gap-2'>
                        <Trans i18nKey={`subscription.fullsocialNetworkSupport`}>All Social Networks</Trans>
                    </span>
                </div> */}
                {/* <div className='flex items-center gap-2'>
                    <HiOutlineSupport className='text-primary-600 text-xl'/> 
                    <span className='group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300'><Trans i18nKey={`subscription.liveChatSupport`}>Live Chat Support </Trans></span>
                </div> */}
            </div>
            {isAIStudioPlan && (
                <div className="flex flex-col gap-2 !text-xs">
                    <div className='flex items-center gap-2'>
                        <TbUserCheck className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'>AI Avatars</span>
                    </div>
                    {/* <div className='flex items-center gap-2'>
                        <TbHeartStar className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'>AI Viral Shorts</span>
                    </div> */}
                    <div className='flex items-center gap-2'>
                        <RiVideoOnAiLine className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'>AI Videos</span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <RiImageAiLine className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'>AI Images</span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <TbCalendarBolt className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'>Schedule Posts</span>
                    </div>
                    <div className='flex items-center gap-2'>
                        <HiOutlineUser className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'><b>{item?.users_amount}</b> <Trans i18nKey={`subscription.productUser`}>User</Trans>{item?.users_amount > 1 && 's'}</span>
                        <span className="group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-300">+</span>
                        <HiOutlineBriefcase className='text-primary-600 group-[.is-active]:!text-primary-500 text-base'/> 
                        <span className='group-[.is-active]:!text-white !text-gray-600 dark:text-gray-300'><b>{item?.companies_amount}</b> {item?.users_amount > 1 ? <Trans i18nKey={`subscription.companies`}>Companies</Trans> : <Trans i18nKey={`subscription.company`}>Company</Trans>}</span>
                    </div>
                </div>
            )}
            <div className="flex flex-1 flex-col items-end gap-4 mt-4 lg:mt-0">
                <div className='flex flex-col gap-0 items-right'>
                    {discountFactor < 1 && 
                        <div className="flex items-center gap-2">
                            <Badge content={`${discountText}% OFF`} />
                            <h1 className='!text-lg group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-100 text-right line-through opacity-[0.4]'>
                                ${formatNumber((item?.prices?.find(e => e.recurring == billingFrequency)?.amount) / (billingFrequency == 'year' ? 12 : 1) / 100 , 0)}
                            </h1>
                        </div>
                    }
                    <h1 className='mega-title !text-3xl group-[.is-active]:!text-tertiary-50 text-tertiary-500 dark:text-gray-100 text-right !font-bold'>
                        ${formatNumber((item?.prices?.find(e => e.recurring == billingFrequency)?.amount) / (billingFrequency == 'year' ? 12 : 1) / 100 * discountFactor, 0)}
                        {/* &nbsp;<small>USD</small> */}
                        <span className='inline-block ml-1 -mt-2 md:hidden text-tertiary-500 dark:text-gray-600 font-semibold !text-sm text-right group-[.is-active]:!text-tertiary-50'>/ month</span>
                    </h1>
                    <h4 className='hidden md:block text-gray-400 dark:text-gray-500 !font-normal mt-0 !text-sm text-right group-[.is-active]:!text-tertiary-50'>/ month</h4>
                </div>
            </div>
        </Card>
    )
}

export default ProductPlan;